import { Aide } from '@/components/ui/aide';
import ShortagePanel, { plaquesTrajet, referenceTrajet } from '../components/ShortagePanel';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { formatNombre, formatPourcent } from '../lib/formats';
import {
  clientsAPI,
  ordersAPI,
  stockMovementsAPI,
  tripsAPI,
  trucksAPI,
  volumeReconciliationsAPI,
} from '../lib/api';
import { fetchAllPages } from '../lib/fetchAllPages';
import { camionsEnTransit } from '../lib/stockTransit';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { SearchableSelect } from '../components/ui/searchable-select';
import { toast } from '../components/ui/sonner';
import {
  cn,
  DEFAULT_STOCK_THRESHOLDS,
  getStockAlert,
  jaugeStock,
  formatCompactCurrency,
  formatCompactVolume,
  formatVolume,
} from '../lib/utils';
import { LEXIQUE } from '../lib/lexique';
import { libelle, referenceLisible, texteSur } from '../lib/libelles';
import { tripOption } from '../lib/selectOptions';
import { messageErreur } from '../lib/erreurs';
import { StatutBadge } from '../components/ui/StatutBadge';
import StockMovementsTable from '../components/stock/StockMovementsTable';
import { BarrePagination } from '../components/ui/pagination-bar';
import { usePaginationServeur } from '../hooks/usePagination';
import { useDerniereDemande } from '../hooks/useDerniereDemande';
import { DernieresLivraisons, livraisonsRecentes } from '../components/stock/DernieresLivraisons';
import { useAuth } from '../context/AuthContext';
import useHasOwnStations from '../hooks/useHasOwnStations';
import BusinessActionContract from '../components/business/BusinessActionContract';
import {
  stockMovementBlockers,
  stockMovementContract,
} from '../lib/businessContracts';
import {
  SectionCard,
  FuelGauge,
} from '../components/dashboard';
import {
  AlertTriangle,
  ArrowRight,
  Container,
  Droplet,
  Fuel,
  Plus,
  RefreshCw,
  Truck,
} from 'lucide-react';
import { PageHeader } from '../components/ui/page-header';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { EmptyState } from '../components/ui/empty-state';
import { LoadingState } from '../components/ui/loading-state';
import { ChampsAvances } from '../components/ui/champs-avances';
import { appliquerPrixPropose, usePrixPropose } from '../hooks/usePrixPropose';
import { useRafraichirSur } from '../hooks/useRafraichirSur';

const NO_LINKED_RECORD = '__none__';

const transportMovementOptions = ['IMPORT', 'SALE', 'INTERNAL_TRANSFER', 'ADJUSTMENT', 'LOSS'].map(
  (code) => [code, libelle('stockMovement', code)],
);

const controlFuelMovementOptions = [
  ['IMPORT', 'Entrée cuve interne'],
  ['INTERNAL_CONSUMPTION', 'Sortie vers engin'],
  ['INTERNAL_TRANSFER', 'Transfert interne'],
  ['ADJUSTMENT', 'Ajustement inventaire'],
  ['LOSS', 'Perte / écart'],
];

const transportLocations = ['DEPOT', 'IN_TRANSIT', 'STATION'].map((code) => [
  code,
  libelle('stockLocation', code),
]);

const controlFuelOrigins = [
  ['INTERNAL_TANK', libelle('stockLocation', 'INTERNAL_TANK')],
  ['DEPOT', 'Dépôt interne'],
];

const controlFuelDestinations = ['ASSET', 'PROJECT', 'DEPARTMENT', 'INTERNAL_TANK'].map((code) => [
  code,
  libelle('stockLocation', code),
]);

/**
 * Le trajet du carburant qu'implique chaque nature de mouvement.
 *
 * Origine et destination sont repliées: le formulaire ne peut donc plus
 * compter sur l'exploitant pour les poser, alors que `stockMovementBlockers`
 * refuse le mouvement sans elles — le bouton « Créer » serait éteint à
 * l'ouverture, pour un motif caché dans le dépliant. Un import arrive au
 * dépôt, une vente en part vers une station, un ajustement n'y déplace rien:
 * la nature du mouvement dit déjà où va le volume. La valeur reste modifiable
 * dans le repli, pour le cas — réel mais minoritaire — d'une livraison directe
 * de camion à station.
 */
/**
 * Les extrémités d'un mouvement, reprises de ce que le serveur écrit lui-même.
 *
 * Ce ne sont pas des conventions choisies ici: `services/stock_automation.py`
 * pose exactement ces couples quand il enregistre un mouvement automatique —
 * transit vers dépôt à l'arrivée d'un camion, dépôt vers station à la
 * livraison d'une commande. Une saisie manuelle doit produire les mêmes
 * écritures qu'un camion qui franchit la frontière, sinon le solde par dépôt
 * dépend de la manière dont le mouvement est entré.
 *
 * Une sortie n'arrive nulle part et une correction à la hausse ne vient de
 * nulle part: `null` n'est pas un oubli. Le grand livre crédite l'arrivée puis
 * débite le départ — donner « dépôt → dépôt » à une perte faisait s'annuler
 * les deux écritures, et le dépôt ne bougeait pas alors qu'il venait de perdre
 * du carburant.
 */
const transportDefaultLocations = {
  IMPORT: ['IN_TRANSIT', 'DEPOT'],
  SALE: ['DEPOT', 'STATION'],
  INTERNAL_TRANSFER: ['DEPOT', 'STATION'],
  ADJUSTMENT: [null, 'DEPOT'],
  LOSS: ['DEPOT', null],
};

const controlFuelDefaultLocations = {
  IMPORT: ['DEPOT', 'INTERNAL_TANK'],
  INTERNAL_CONSUMPTION: ['INTERNAL_TANK', null],
  INTERNAL_TRANSFER: ['INTERNAL_TANK', 'INTERNAL_TANK'],
  ADJUSTMENT: [null, 'INTERNAL_TANK'],
  LOSS: ['INTERNAL_TANK', null],
};

const defaultLocations = (movementType, isControlFuel) => {
  const table = isControlFuel
    ? controlFuelDefaultLocations
    : transportDefaultLocations;
  return table[movementType] || (isControlFuel ? ['INTERNAL_TANK', null] : ['DEPOT', null]);
};

const initialFormData = (isControlFuel) => {
  const type = isControlFuel ? 'INTERNAL_CONSUMPTION' : 'IMPORT';
  const [from, to] = defaultLocations(type, isControlFuel);
  return {
    movement_type: type,
    quantity_liters: '',
    unit_cost: '',
    //  devient chaîne vide: le champ est contrôlé, et React refuse de
    // passer d'une valeur non contrôlée à contrôlée en cours de saisie.
    from_location: from || '',
    to_location: to || '',
    // Vide: le serveur date le mouvement de son enregistrement. Le champ ne
    // sert qu'à antidater une saisie de rattrapage, et vit dans le repli.
    movement_date: '',
    reference_number: '',
    notes: '',
    trip_id: '',
    order_id: '',
  };
};

/* ------------------------------------------------------------------ */
/* La ligne de chiffres                                                 */
/* ------------------------------------------------------------------ */

// Les écarts qui attendent une décision (même liste que le serveur).
const ECARTS_EN_ATTENTE = ['blocked', 'pending_approval', 'warning'];

const StockPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const varianceTripId = searchParams.get('trip_id');
  // « Voir détails » de la carte Stock global descend au journal: le détail du
  // stock global, c'est la liste des mouvements qui le composent, et elle est
  // déjà sur cet écran. Ouvrir une autre page pour y revenir aussitôt ferait
  // perdre le contexte (onglet, recherche) que l'exploitant vient de poser.
  const journalRef = useRef(null);
  const { activePack, hasModule, managesDepot } = useAuth();
  // Les modules station sont ouverts par défaut pour une société transport sans
  // station: la carte « Stations service » ne s'affiche que si elle en a.
  const aDesStations = useHasOwnStations();
  const isControlFuel = activePack === 'CONTROLE_CARBURANT'
    || (hasModule('fuel_issues') && !hasModule('trips'));
  // Société sans dépôt: son stock est le carburant chargé sur les camions et
  // pas encore livré (calculé par le serveur). Ni jauge, ni seuil bas, ni
  // « importés ».
  const sansDepot = !isControlFuel && managesDepot === false;
  // Sans dépôt, l'emplacement « dépôt » du grand livre est le carburant entré
  // au pays et pas encore livré: l'exploitant ne lit pas « Dépôt ».
  const emplacementsTransport = sansDepot
    ? transportLocations.map(([code, label]) => [code, code === 'DEPOT' ? 'Entré, à livrer' : label])
    : transportLocations;
  // `subtitle` a disparu des deux jeux: « Suivi des mouvements de stock
  // importés et distribués » sous un titre « Stocks » n'apprenait rien à qui
  // venait de cliquer sur « Stocks » dans le menu.
  const pageCopy = isControlFuel
    ? {
        title: 'Stock carburant interne',
        newButton: 'Nouvelle sortie / entrée',
        recentTitle: 'Journal stock interne',
        dialogTitle: 'Nouveau mouvement interne',
        editTitle: 'Modifier le mouvement interne',
      }
    : {
        title: sansDepot ? LEXIQUE.menuStockSansDepot : 'Stock de carburant',
        newButton: 'Nouveau mouvement',
        recentTitle: 'Mouvements récents',
        dialogTitle: 'Nouveau mouvement',
        editTitle: 'Modifier le mouvement',
      };
  const [movements, setMovements] = useState([]);
  const [linkedTrips, setLinkedTrips] = useState([]);
  const [linkedTrucks, setLinkedTrucks] = useState([]);
  const [linkedOrders, setLinkedOrders] = useState([]);
  const [linkedClients, setLinkedClients] = useState([]);
  const [summary, setSummary] = useState(null);
  const [stockReadiness, setStockReadiness] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lowThreshold] = useState(
    String(DEFAULT_STOCK_THRESHOLDS.low),
  );
  const [highThreshold] = useState(
    String(DEFAULT_STOCK_THRESHOLDS.high),
  );
  const [searchQuery, setSearchQuery] = useState('');
  // La nature de mouvement mise en avant depuis une autre zone de l'écran.
  const [typeFilter, setTypeFilter] = useState(null);
  const [reconciliations, setReconciliations] = useState([]);
  const [isLoadingVariances, setIsLoadingVariances] = useState(true);
  // Les écarts se lisent par pages: le total et le nombre d'écarts en attente
  // de décision (pastille de l'onglet) viennent du serveur, qui voit tous les
  // écarts, pas la seule page affichée. `ecarts-page` / `ecarts-taille` dans
  // l'adresse, pour ne pas se mêler à la pagination du journal.
  const [varianceTotal, setVarianceTotal] = useState(0);
  const [variancePending, setVariancePending] = useState(null);
  const variancePagination = usePaginationServeur(varianceTotal, {
    cle: 'ecarts',
    reinitialiser: varianceTripId || '',
  });
  const { page: variancePage, limit: variancePageSize } = variancePagination;
  const [varianceDecision, setVarianceDecision] = useState(null);

  const linkedClientsById = useMemo(
    () => new Map(linkedClients.map((client) => [client.id, client])),
    [linkedClients],
  );
  const [showNewModal, setShowNewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedMovement, setSelectedMovement] = useState(null);
  const [formData, setFormData] = useState(() => initialFormData(isControlFuel));

  // Ouvrir « Nouveau mouvement » remet le formulaire à neuf. Les trois boutons
  // qui y mènent se contentaient d'afficher la modale, et l'état restait celui
  // de la modification qu'on venait de fermer: le dépliant s'ouvrait tout seul
  // en annonçant « renseigné », et un mouvement neuf naissait avec la
  // référence et les notes d'un autre.
  const ouvrirNouveauMouvement = () => {
    setFormData(initialFormData(isControlFuel));
    setShowNewModal(true);
  };

  // Le coût au litre était retapé à chaque mouvement alors que les barèmes de
  // la société le portaient déjà. Le barème propose, il n'impose pas: dès que
  // l'exploitant corrige le champ, plus rien ne le réécrit.
  const [coutTouche, setCoutTouche] = useState(false);
  const [emplacementsTouches, setEmplacementsTouches] = useState(false);
  const { prix: prixPropose, nomBareme } = usePrixPropose({
    operationType: formData.movement_type,
    actif: showNewModal,
  });

  // Une correction faite sur un mouvement ne doit pas éteindre la proposition
  // pour le suivant: chaque ouverture repart d'une feuille blanche.
  useEffect(() => {
    if (!showNewModal) return;
    setCoutTouche(false);
    setEmplacementsTouches(false);
  }, [showNewModal]);

  useEffect(() => {
    if (!showNewModal) return;
    setFormData((prev) => ({
      ...prev,
      unit_cost: appliquerPrixPropose({
        prix: prixPropose,
        valeurActuelle: prev.unit_cost,
        toucheParLHumain: coutTouche,
      }),
    }));
  }, [prixPropose, coutTouche, showNewModal]);

  // Changer la nature du mouvement redessine son trajet, tant que personne
  // n'est allé poser origine et destination à la main.
  const changerTypeMouvement = (value) =>
    setFormData((prev) => {
      if (emplacementsTouches) return { ...prev, movement_type: value };
      const [from, to] = defaultLocations(value, isControlFuel);
      return { ...prev, movement_type: value, from_location: from, to_location: to };
    });

  const loadMovements = async ({ silencieux = false } = {}) => {
    if (!silencieux) setIsLoading(true);
    try {
      // Tout le journal: le coût moyen au litre et la recherche portent sur
      // tous les mouvements, pas sur les deux cents derniers.
      const result = await fetchAllPages((params) => stockMovementsAPI.getAllPaged(params));
      setMovements(result.items || []);
    } catch (err) {
      console.error('Failed to load stock movements:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      const summaryData = await stockMovementsAPI.getSummary();
      setSummary(summaryData);
    } catch (err) {
      console.error('Failed to load stock summary:', err);
    }
  };

  const loadReadiness = useCallback(async () => {
    try {
      const readiness = await stockMovementsAPI.getReadiness({
        low_threshold: Number(lowThreshold || DEFAULT_STOCK_THRESHOLDS.low),
        high_threshold: Number(highThreshold || DEFAULT_STOCK_THRESHOLDS.high),
      });
      setStockReadiness(readiness);
    } catch (err) {
      console.error('Failed to load stock readiness:', err);
    }
  }, [highThreshold, lowThreshold]);

  const loadLinkedOptions = useCallback(async () => {
    if (isControlFuel) {
      setLinkedTrips([]);
      setLinkedOrders([]);
      setLinkedClients([]);
      return;
    }

    try {
      // Chaque liste est indépendante: une commande ou un client en échec ne
      // doit pas priver les cartes de leurs trajets et de leurs camions.
      const vide = () => ({ items: [] });
      const [trips, ordersResult, clientsResult, trucksResult] = await Promise.all([
        tripsAPI.getAll().catch(() => []),
        fetchAllPages((page) => ordersAPI.getAllPaged(page)).catch(vide),
        fetchAllPages((page) => clientsAPI.getAllPaged(page)).catch(vide),
        // La plaque des camions en transit: le résumé ne porte que le trajet.
        fetchAllPages((page) => trucksAPI.getAllPaged(page)).catch(vide),
      ]);
      setLinkedTrips(Array.isArray(trips) ? trips : []);
      setLinkedTrucks(trucksResult?.items || []);
      setLinkedOrders(ordersResult.items || []);
      setLinkedClients(clientsResult.items || []);
    } catch (err) {
      console.error('Failed to load linked stock options:', err);
    }
  }, [isControlFuel]);

  const nouvelleDemandeEcarts = useDerniereDemande();
  const loadReconciliations = useCallback(async () => {
    const perimee = nouvelleDemandeEcarts();
    setIsLoadingVariances(true);
    try {
      const result = await volumeReconciliationsAPI.getPage({
        page: variancePage,
        page_size: variancePageSize,
        ...(varianceTripId ? { trip_id: varianceTripId } : {}),
      });
      if (perimee()) return;
      setReconciliations(result.items);
      setVarianceTotal(result.total);
      setVariancePending(result.pendingCount);
    } catch (err) {
      if (perimee()) return;
      console.error('Failed to load volume reconciliations:', err);
    } finally {
      if (!perimee()) setIsLoadingVariances(false);
    }
  }, [variancePage, variancePageSize, varianceTripId, nouvelleDemandeEcarts]);

  useEffect(() => {
    loadMovements();
    loadSummary();
    loadReadiness();
    loadLinkedOptions();
  }, [loadLinkedOptions, loadReadiness]);

  // Les écarts se rechargent à chaque page, taille ou trajet choisi — sans
  // refaire le journal, le résumé ni les listes liées.
  useEffect(() => {
    loadReconciliations();
  }, [loadReconciliations]);

  // Une écriture faite ailleurs (autre écran, autre onglet, file hors ligne rejouée) recharge ces chiffres: jamais de compteur figé.
  useRafraichirSur(['stock', 'trajets', 'commandes'], (options) => Promise.all([loadMovements(options), loadSummary(options), loadReadiness(options), loadReconciliations(options)]));


  const openVarianceDecision = (reconciliation, action) =>
    setVarianceDecision({ reconciliation, action });

  // La décision remplace la ligne en place plutôt que de recharger l'onglet:
  // recharger repositionnerait la table et ferait perdre la ligne qu'on vient
  // de traiter.
  const submitVarianceDecision = async (reason) => {
    if (!varianceDecision) return;
    const { reconciliation, action } = varianceDecision;
    const call =
      action === 'approve'
        ? volumeReconciliationsAPI.approve
        : volumeReconciliationsAPI.reject;
    try {
      const updated = await call(reconciliation.id, { comment: reason, reason });
      setReconciliations((prev) =>
        prev.map((item) => (item.id === updated.id ? updated : item)),
      );
      // Un écart tranché n'attend plus de décision: la pastille suit.
      if (
        ECARTS_EN_ATTENTE.includes(reconciliation.status) &&
        !ECARTS_EN_ATTENTE.includes(updated.status)
      ) {
        setVariancePending((n) => (n == null ? n : Math.max(0, n - 1)));
      }
      toast.success(
        action === 'approve' ? 'Écart approuvé' : 'Écart refusé, motif enregistré',
      );
    } catch (err) {
      toast.error(messageErreur(err, 'La décision n’a pas pu être enregistrée'));
    } finally {
      setVarianceDecision(null);
    }
  };

  const pendingVarianceCount =
    variancePending ??
    reconciliations.filter((item) => ECARTS_EN_ATTENTE.includes(item.status)).length;

  // Les totaux viennent du serveur, qui voit tous les mouvements. Les
  // recalculer ici les calculait sur la seule page affichée: le chiffre
  // changeait en tournant les pages, et divergeait du résumé.
  const totals = {
    imports: Number(summary?.imports || 0),
    sales: Number(summary?.sales || 0),
    internal: Number(summary?.transfers || 0),
    consumption: Number(summary?.internal_consumption || 0),
    loss: Number(summary?.losses || 0),
    adjustment: Number(summary?.adjustments || 0),
  };

  const currentStock = Number(summary?.available_stock_liters || 0);

  const stockAlert = getStockAlert(currentStock, {
    low: Number(lowThreshold || DEFAULT_STOCK_THRESHOLDS.low),
    high: Number(highThreshold || DEFAULT_STOCK_THRESHOLDS.high),
  });

  const avgUnitCost = movements
    .filter((movement) => movement.movement_type === 'IMPORT')
    .reduce(
      (acc, movement) => {
        const qty = Number(movement.quantity_liters || 0);
        const cost = Number(movement.unit_cost || 0);
        acc.totalQty += qty;
        acc.totalCost += qty * cost;
        return acc;
      },
      { totalQty: 0, totalCost: 0 },
    );
  const cmup =
    avgUnitCost.totalQty > 0 ? avgUnitCost.totalCost / avgUnitCost.totalQty : 0;

  const enTransit = Number(summary?.in_transit_liters || 0);
  // `/stock/summary` ne renvoie ces deux regroupements que pour le transport;
  // en contrôle carburant il n'y a ni camion tiers ni station à servir.
  const camionsRemplissage = isControlFuel
    ? []
    : camionsEnTransit(summary?.by_truck, linkedTrips, linkedTrucks);
  const stationsLivrees = isControlFuel ? [] : summary?.by_station || [];

  // La jauge a besoin d'un plafond. Aucune capacité de cuve n'est déclarée
  // côté API: le cumul importé sert de référence, et à défaut le seuil haut —
  // exactement le repli du tableau de bord, sans quoi les deux écrans
  // afficheraient deux pourcentages différents du même stock.
  const { niveau: niveauCuve, capacite: capaciteSuivie } = jaugeStock(
    currentStock,
    totals.imports,
  );
  // Sans dépôt: capacité des citernes des camions chargés en route; si un
  // camion n'est pas retrouvé, le chargé lui-même sert de plancher.
  const capaciteEnRoute = (() => {
    const total = camionsRemplissage.reduce((somme, ligne) => {
      const trajet = (linkedTrips || []).find((t) => t && t.id === ligne.trip_id);
      const camion = (linkedTrucks || []).find(
        (c) => c && (c.id === (ligne.truck_id || trajet?.truck_id) || (ligne.matricule && c.matricule === ligne.matricule)),
      );
      return somme + (Number(camion?.capacity_liters) || 0);
    }, 0);
    return Math.max(total, enTransit);
  })();
  const niveauEnRoute = capaciteEnRoute > 0 ? (enTransit / capaciteEnRoute) * 100 : 0;
  // La jauge parle en état, pas en valeur: c'est le verdict de `getStockAlert`
  // qui la colore, le même qui colore l'encadré « Stock bas » plus bas.
  const etatCuve =
    stockAlert.level === 'low'
      ? 'bas'
      : stockAlert.level === 'high'
        ? 'haut'
        : 'normal';

  // Le coût moyen de l'API prime sur le CMUP recalculé ici: l'API voit tous
  // les mouvements, cette page n'en a que 200. `null` quand aucun mouvement
  // n'est valorisé — on affiche alors « — » plutôt qu'un zéro trompeur.
  const coutMoyenImport =
    stockReadiness?.metrics?.average_unit_cost ?? (cmup > 0 ? cmup : null);

  // Les six chiffres qui coiffaient l'écran sur huit encadrés. Ordre du
  // carburant, pas ordre de l'API: ce qui entre, ce qui roule, ce qui sort, ce
  // qui se perd, ce qui reste, ce que ça coûte.
  const chiffresStock = [
    ...(sansDepot ? [] : [{
      cle: 'entrees',
      valeur: formatCompactVolume(totals.imports),
      libelle: isControlFuel ? 'entrés en cuve' : 'importés',
    }]),
    ...(!isControlFuel && !sansDepot
      ? [
          {
            cle: 'transit',
            valeur: formatCompactVolume(enTransit),
            libelle: 'en transit',
          },
        ]
      : []),
    {
      cle: 'sorties',
      valeur: formatCompactVolume(
        isControlFuel
          ? totals.consumption || totals.internal
          : totals.sales + totals.internal + totals.consumption,
      ),
      libelle: isControlFuel ? 'sortis vers les engins' : sansDepot ? 'livrés' : 'sortis',
    },
    {
      cle: 'pertes',
      valeur: formatCompactVolume(totals.loss),
      libelle: 'de pertes et écarts',
      alerte: totals.loss > 0,
    },
    {
      cle: 'stock',
      // Le verdict de `getStockAlert` accompagne le volume plutôt que de
      // vivre dans un encadré à part: « 12 000 L en stock » ne dit pas si
      // c'est peu.
      valeur: formatCompactVolume(currentStock),
      libelle: sansDepot
        ? 'chargés, non livrés'
        : `en stock — ${stockAlert.label.toLowerCase()}`,
      alerte: !sansDepot && stockAlert.level === 'low',
    },
    {
      cle: 'cout',
      valeur: coutMoyenImport
        ? `${formatCompactCurrency(coutMoyenImport)} / L`
        : '—',
      libelle: isControlFuel
        ? 'de coût moyen interne'
        : sansDepot
          ? 'de prix d’achat moyen'
          : 'de coût moyen à l’import',
    },
    // Ex-« Readiness stock », et son anneau de pourcentage. Le score reste, le
    // mot anglais et le cercle décoratif non.
    ...(stockReadiness
      ? [
          {
            cle: 'controle',
            valeur: `${stockReadiness.score}%`,
            libelle: stockReadiness.ready
              ? LEXIQUE.pret.toLowerCase()
              : LEXIQUE.aCorriger.toLowerCase(),
            alerte: !stockReadiness.ready,
          },
        ]
      : []),
  ];

  const linkedTripOptions = useMemo(
    () => [
      { value: NO_LINKED_RECORD, label: 'Aucun trajet lié' },
      ...linkedTrips.map((trip) => {
        const option = tripOption(trip);
        const volume = formatVolume(trip.fuel_quantity_real || trip.allocated_quantity || trip.fuel_quantity_estimated || 0);
        return { ...option, description: [option.description, volume].filter(Boolean).join(' · ') };
      }),
    ],
    [linkedTrips],
  );

  const linkedOrderOptions = useMemo(
    () => [
      { value: NO_LINKED_RECORD, label: 'Aucune commande liée' },
      ...linkedOrders.map((order) => {
        const clientName = linkedClientsById.get(order.client_id)?.name || 'Client non renseigné';
        const reference = referenceLisible(order.reference_number);
        const status = order.progress?.state
          ? libelle('etat_commande', order.progress.state)
          : libelle('statut_commande', order.status, 'état non renseigné');
        // Miroir de `remaining_order_volume()` côté serveur. La colonne
        // `remaining_quantity` n'a jamais été maintenue et a été retirée des
        // réponses; retomber sur `quantity_liters` annonçait le volume total
        // comme restant, même sur une commande déjà livrée aux trois quarts.
        const allocated = (order.allocated_trips || []).reduce(
          (sum, item) => sum + Number(item?.quantity || 0),
          0,
        );
        const remaining = Math.max(
          0,
          Number(order.quantity_liters || 0) -
            Number(order.delivered_quantity || 0) -
            allocated,
        );
        return {
          value: order.id,
          label: `${reference} · ${clientName}`,
          description: `reste ${formatVolume(remaining)} · ${status}`,
          keywords: [order.id],
        };
      }),
    ],
    [linkedOrders, linkedClientsById],
  );
  const movementContract = stockMovementContract({
    isControlFuel,
    movementType: formData.movement_type,
  });
  const movementBlockers = stockMovementBlockers(formData);
  const stockMissions = [
    ...(stockReadiness ? [{
      title: stockReadiness.ready ? LEXIQUE.pret : `Stock ${LEXIQUE.aDebloquer.toLowerCase()}`,
      description: texteSur(
        stockReadiness.blocking_reasons?.[0]?.message || stockReadiness.business_context?.next_action,
        'Le stock est cohérent.',
      ),
      tone: stockReadiness.ready ? 'ok' : 'risk',
      count: `${stockReadiness.score || 0}%`,
    }] : []),
    sansDepot ? {
      title: LEXIQUE.menuStockSansDepot,
      description: 'Chargé sur les camions, pas encore livré.',
      tone: 'info',
      count: formatCompactVolume(currentStock),
    } : {
      title: stockAlert.level === 'normal' ? 'Stock maîtrisé' : stockAlert.label,
      description: stockAlert.message,
      tone: stockAlert.level === 'low' ? 'risk' : stockAlert.level === 'high' ? 'warning' : 'ok',
      // Pastille = tuile: le volume y est abrégé. Le litre exact se lit dans
      // le tableau des mouvements, seul endroit où il engage quelqu'un.
      count: formatCompactVolume(currentStock),
      action: pageCopy.newButton,
      onClick: ouvrirNouveauMouvement,
    },
    {
      title: isControlFuel ? 'Sorties internes' : 'Mouvements à tracer',
      description: isControlFuel
        ? 'Chaque sortie vers engin doit porter quantité, bénéficiaire et coût si connu.'
        : 'Chaque mouvement doit rester lié au trajet ou à la commande qui l’explique, quand ils existent.',
      tone: totals.loss > 0 ? 'warning' : 'info',
      count: formatCompactVolume(isControlFuel ? totals.consumption : totals.internal + totals.sales),
    },
    {
      title: 'Écarts et pertes',
      description: totals.loss > 0 ? 'Des pertes existent: elles doivent être expliquées et validées.' : 'Aucune perte de stock enregistrée.',
      tone: totals.loss > 0 ? 'risk' : 'ok',
      count: formatCompactVolume(totals.loss),
      action: totals.loss > 0 ? 'Filtrer les pertes' : undefined,
      onClick: totals.loss > 0 ? () => setTypeFilter('LOSS') : undefined,
    },
  ];

  // En-tête: le stock, ce qui entre, ce qui sort, ce qui se perd. Le reste
  // (transit, coût moyen, état du contrôle) est dans « Plus d'indicateurs ».
  const chiffreStock = (cle) => chiffresStock.find((c) => c.cle === cle);
  const versStat = (c, label, ton) =>
    c && {
      label,
      valeur: c.valeur,
      ton: c.alerte ? 'critique' : ton || 'neutre',
      aide: c.libelle,
    };
  const statsEntete = [
    versStat(
      chiffreStock('stock'),
      sansDepot ? 'En route' : 'En stock',
      !sansDepot && stockAlert.level === 'high' ? 'attention' : 'positif',
    ),
    versStat(chiffreStock('entrees'), isControlFuel ? 'Entrés en cuve' : 'Importés'),
    versStat(chiffreStock('sorties'), 'Sortis'),
    versStat(chiffreStock('pertes'), 'Pertes et écarts'),
  ].filter(Boolean);
  const statsPlusEntete = (
    <dl className="space-y-2 text-sm">
      {chiffresStock
        .filter((c) => ['transit', 'cout', 'controle'].includes(c.cle))
        .map((c) => (
          <div key={c.cle} className="flex items-baseline justify-between gap-3">
            <dt className="text-muted-foreground">{c.libelle}</dt>
            <dd
              className={cn(
                'font-bold tabular-nums',
                c.alerte ? 'text-destructive' : 'text-foreground',
              )}
            >
              {c.valeur}
            </dd>
          </div>
        ))}
    </dl>
  );
  // Ce qui demande une décision (risque ou surveillance), en pastilles.
  const alertesStock = stockMissions
    .filter((m) => m.tone === 'risk' || m.tone === 'warning')
    .map((m) => ({
      niveau: m.tone === 'risk' ? 'critique' : 'attention',
      court: m.title,
      texte: [m.description, m.count !== undefined ? `(${m.count})` : null]
        .filter(Boolean)
        .join(' '),
      action: m.onClick && m.action ? { label: m.action, onClick: m.onClick } : undefined,
    }));

  const handleCreate = async () => {
    if (!formData.quantity_liters) return;

    const payload = {
      movement_type: formData.movement_type,
      quantity_liters: Number(formData.quantity_liters || 0),
      unit_cost: formData.unit_cost ? Number(formData.unit_cost) : undefined,
      from_location: formData.from_location || undefined,
      to_location: formData.to_location || undefined,
      movement_date: formData.movement_date || undefined,
      reference_number: formData.reference_number || undefined,
      notes: formData.notes || undefined,
      trip_id: isControlFuel ? undefined : formData.trip_id || undefined,
      order_id: isControlFuel ? undefined : formData.order_id || undefined,
      source: isControlFuel ? 'CONTROL_FUEL' : 'MANUAL',
    };

    try {
      const created = await stockMovementsAPI.create(payload);
      setMovements((prev) => [created, ...prev]);
      setShowNewModal(false);
      setFormData(initialFormData(isControlFuel));
      toast.success('Mouvement enregistré');
    } catch (err) {
      toast.error(messageErreur(err, 'Le mouvement n’a pas pu être enregistré'));
    }
  };

  const openEditModal = (movement) => {
    setSelectedMovement(movement);
    setFormData({
      movement_type: movement.movement_type || (isControlFuel ? 'INTERNAL_CONSUMPTION' : 'IMPORT'),
      quantity_liters: movement.quantity_liters || '',
      unit_cost: movement.unit_cost || '',
      from_location: movement.from_location || (isControlFuel ? 'INTERNAL_TANK' : ''),
      to_location: movement.to_location || (isControlFuel ? 'ASSET' : ''),
      movement_date: movement.movement_date || '',
      reference_number: movement.reference_number || '',
      notes: movement.notes || '',
      trip_id: movement.trip_id || '',
      order_id: movement.order_id || '',
    });
    setShowEditModal(true);
  };

  const handleUpdate = async () => {
    if (!selectedMovement) return;
    if (!formData.quantity_liters) return;

    const payload = {
      movement_type: formData.movement_type,
      quantity_liters: Number(formData.quantity_liters || 0),
      unit_cost: formData.unit_cost ? Number(formData.unit_cost) : undefined,
      from_location: formData.from_location || undefined,
      to_location: formData.to_location || undefined,
      movement_date: formData.movement_date || undefined,
      reference_number: formData.reference_number || undefined,
      notes: formData.notes || undefined,
      trip_id: isControlFuel ? undefined : formData.trip_id || undefined,
      order_id: isControlFuel ? undefined : formData.order_id || undefined,
      source: isControlFuel ? 'CONTROL_FUEL' : undefined,
    };

    try {
      const updated = await stockMovementsAPI.update(
        selectedMovement.id,
        payload,
      );
      setMovements((prev) =>
        prev.map((item) => (item.id === updated.id ? updated : item)),
      );
      setShowEditModal(false);
      setSelectedMovement(null);
      toast.success('Mouvement modifié');
    } catch (err) {
      toast.error(messageErreur(err, 'Le mouvement n’a pas pu être modifié'));
    }
  };

  const confirmDelete = (movement) => {
    setSelectedMovement(movement);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!selectedMovement) return;
    try {
      await stockMovementsAPI.delete(selectedMovement.id);
      setMovements((prev) => prev.filter((m) => m.id !== selectedMovement.id));
      toast.success('Mouvement supprimé');
    } catch (err) {
      toast.error(messageErreur(err, 'Le mouvement n’a pas pu être supprimé'));
    } finally {
      setShowDeleteModal(false);
      setSelectedMovement(null);
    }
  };

  const allerAuJournal = () =>
    journalRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const dernieresLivraisons = livraisonsRecentes({
    mouvements: movements,
    trajets: linkedTrips,
    camions: linkedTrucks,
    commandes: linkedOrders,
    clients: linkedClients,
  });
  const contratMouvement = stockReadiness?.action_contract ? (
    <BusinessActionContract
      title={`${LEXIQUE.conditionsAction} un mouvement de stock`}
      contract={stockReadiness.action_contract}
      blockers={(stockReadiness.blocking_reasons || []).map((item) => item.message)}
      compact
    />
  ) : null;

  return (
    <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
      <PageHeader
        eyebrow="Stock"
        title={pageCopy.title}
        stats={statsEntete}
        statsPlus={statsPlusEntete}
        alertes={alertesStock}
        // Sous le titre : à droite, avec les boutons et quatre chiffres, les
        // deux alertes chevauchaient les chiffres (mode sans dépôt).
        alertesSousTitre
        actions={
          <>
            {!isControlFuel && (
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    const result = await stockMovementsAPI.recompute();
                    toast.success(`Recalculé : ${result.created} créés, ${result.updated} mis à jour`);
                    loadSummary();
                    loadMovements();
                  } catch (err) {
                    toast.error(messageErreur(err, 'Le recalcul du stock a échoué'));
                  }
                }}
              >
                <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
                Recalculer
              </Button>
            )}
            <Button onClick={ouvrirNouveauMouvement}>
              <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
              {pageCopy.newButton}
            </Button>
          </>
        }
      />

      {/* Trois cartes de situation, dans l'ordre où le carburant se déplace:
          ce qui arrive, ce qui est en cuve, ce qui est parti. L'ordre des
          champs de l'API n'a rien à dire ici. */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <SectionCard
          className="flex flex-col"
          titreMultiligne
          icon={Truck}
          title={isControlFuel ? 'Cuves internes' : sansDepot ? 'Camions chargés, en route' : 'Camions en cours de chargement'}
          actionLabel={isControlFuel ? undefined : 'Voir les trajets'}
          onAction={isControlFuel ? undefined : () => navigate('/trips')}
          contentClassName="flex flex-1 flex-col space-y-3"
        >
          {camionsRemplissage.slice(0, 4).map((camion, index) => (
            <div
              key={camion.trip_id || index}
              className="flex items-center gap-3 rounded-xl border border-border/70 bg-background px-3 py-2.5"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Truck className="h-[18px] w-[18px]" aria-hidden="true" />
              </span>
              <div className="min-w-0 flex-1">
                {/* Le résumé regroupe par trajet et ne porte pas toujours
                    l'immatriculation: jamais l'identifiant à la place d'une
                    plaque, l'écran dit simplement qu'il ne connaît pas le camion. */}
                <p className="truncate text-sm font-bold text-foreground">
                  {camion.libelle}
                </p>
                <p className="text-xs text-muted-foreground">
                  {formatCompactVolume(Number(camion.in_transit_liters) || 0)}{' '}
                  {sansDepot ? 'à livrer' : 'en transit'}
                </p>
              </div>
              <ArrowRight
                className="h-4 w-4 shrink-0 text-muted-foreground/60"
                aria-hidden="true"
              />
            </div>
          ))}

          {isControlFuel ? (
            <div className="rounded-xl border border-flame/25 bg-flame/[0.07] p-3.5">
              <div className="flex items-center gap-1">
                <p className="text-sm font-bold text-foreground">
                Stock interne disponible
              </p>
                <Aide titre="Stock interne disponible">Les entrées alimentent vos cuves internes; les sorties carburant vers engins diminuent le stock.</Aide>
              </div>
            </div>
          ) : (
            camionsRemplissage.length === 0 && (
              <p className="py-4 text-center text-sm text-muted-foreground">
                Aucun camion en cours
              </p>
            )
          )}

          {/* L'encadré dit où est ce carburant et quand il change de compte:
              avec dépôt, il entre dans le stock disponible à la réception;
              sans dépôt, il sort du stock à la livraison. */}
          {!isControlFuel && enTransit > 0 && (
            <div className="rounded-xl border border-primary/20 bg-primary/[0.05] p-3.5">
              <div className="flex items-center gap-1">
                <p className="text-sm font-bold text-foreground">
                  {sansDepot
                    ? `${formatCompactVolume(enTransit)} ${enTransit >= 2 ? 'chargés' : 'chargé'}, en route vers le client`
                    : `${formatCompactVolume(enTransit)} ${enTransit >= 2 ? 'chargés' : 'chargé'}, en route vers votre dépôt`}
                </p>
                <Aide titre="Carburant en route">
                  {sansDepot
                    ? `${enTransit >= 2 ? 'Ils sortiront' : 'Il sortira'} du stock à la livraison.`
                    : `${enTransit >= 2 ? 'Ils seront disponibles' : 'Il sera disponible'} à la vente dès ${enTransit >= 2 ? 'leur' : 'son'} arrivée au dépôt.`}
                </Aide>
              </div>
            </div>
          )}
        </SectionCard>

        <SectionCard
          className="flex flex-col"
          icon={Container}
          title={isControlFuel ? 'Stock interne' : 'Stock global'}
          actionLabel="Voir les mouvements"
          onAction={allerAuJournal}
          contentClassName="flex flex-1 flex-col space-y-4"
        >
          {/* Même cadran dans les deux modes: seules les valeurs changent. Sans
              dépôt, il mesure le carburant chargé et pas encore livré, rapporté
              à la capacité des citernes en route. */}
          <FuelGauge
            anime
            level={sansDepot ? niveauEnRoute : niveauCuve}
            etat={sansDepot ? 'normal' : etatCuve}
            legende={sansDepot ? 'des citernes en route remplies' : 'de la capacité suivie'}
            valeurLegende={
              sansDepot
                ? `${formatCompactVolume(enTransit)} / ${formatCompactVolume(capaciteEnRoute)}`
                : `${formatCompactVolume(currentStock)} / ${formatCompactVolume(capaciteSuivie)}`
            }
          />

          {sansDepot && (
            <div className="text-center">
              <p className="text-sm font-bold text-foreground">
                {formatVolume(enTransit)} {enTransit >= 2 ? 'chargés' : 'chargé'}, non {enTransit >= 2 ? 'livrés' : 'livré'}
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                sur {camionsRemplissage.length} camion{camionsRemplissage.length > 1 ? 's' : ''} en route
              </p>
            </div>
          )}

          {/* Ce qui autorise ou bloque un mouvement: au pied de la carte du
              stock, sur toute sa largeur. */}
          {contratMouvement && <div className="mt-auto border-t border-border/70 pt-4">{contratMouvement}</div>}
        </SectionCard>

        {!isControlFuel && !(hasModule('stations') && aDesStations) ? (
        <SectionCard
          icon={Truck}
          title="Dernières livraisons"
          actionLabel="Voir les mouvements"
          onAction={allerAuJournal}
        >
          <DernieresLivraisons livraisons={dernieresLivraisons} />
        </SectionCard>
        ) : (
        <SectionCard
          icon={isControlFuel ? Fuel : Droplet}
          title={isControlFuel ? 'Sorties carburant' : 'Stations service'}
          actionLabel={isControlFuel ? undefined : 'Voir les stations'}
          onAction={isControlFuel ? undefined : () => navigate('/stations')}
          contentClassName="space-y-3"
        >
          {stationsLivrees.slice(0, 4).map((station, index) => (
            <div
              key={station.station_id || index}
              className="flex items-center gap-3 rounded-xl border border-border/70 bg-background px-3 py-2.5"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Droplet className="h-[18px] w-[18px]" aria-hidden="true" />
              </span>
              <div className="min-w-0 flex-1">
                {/* Même limite que pour les camions: le résumé groupe par
                    `station_id` et ne renvoie pas le nom de la station. */}
                <p className="truncate text-sm font-bold text-foreground">
                  {station.name || 'Station'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {formatCompactVolume(Number(station.delivered_liters || 0))}{' '}
                  livrés
                </p>
              </div>
            </div>
          ))}

          {isControlFuel ? (
            <div className="rounded-xl border border-primary/20 bg-primary/[0.05] p-3.5">
              <div className="flex items-center gap-1">
                <p className="text-sm font-bold text-foreground">
                  {formatCompactVolume(totals.consumption || totals.internal)} sortis
                </p>
                <Aide titre="Litres sortis">
                  Consommation interne enregistrée depuis les demandes et sorties
                  carburant.
                </Aide>
              </div>
            </div>
          ) : (
            stationsLivrees.length === 0 && (
              <div className="flex flex-col items-center justify-center gap-3 py-5 text-center">
                {/* Une station dessinée plutôt qu'une phrase grise seule: la
                    carte est vide au premier jour d'un client, et le dessin dit
                    ce qu'elle contiendra. La pastille reste neutre — « vide »
                    n'est pas une alerte. */}
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-muted text-muted-foreground">
                  <Fuel className="h-7 w-7" aria-hidden="true" />
                </span>
                <p className="text-sm text-muted-foreground">
                  Aucune livraison récente
                </p>
              </div>
            )
          )}
        </SectionCard>
        )}
      </div>


      {/* Ici vivaient les deux rangées d'encadrés et, entre elles, un bandeau
          « Mission stock distribution / Décisions stock carburant / Garder un
          stock central fiable ». Trois lignes de préambule au-dessus de
          chiffres qui, eux, disent réellement où en est le stock: la seule
          donnée du bandeau était sa pastille « N à surveiller », que la ligne
          « À surveiller » juste dessous détaille point par point. */}

      {/* Une ligne, et seulement ce qui appelle un geste. Les quatre encadrés
          teintés d'avant répétaient les tuiles juste au-dessus, occupaient
          autant de place pour dire « tout va bien » que pour signaler une
          perte, et se contredisaient: « Readiness API validée » s'affichait en
          vert, avec une coche, au-dessus de « Des pertes sont enregistrées et
          doivent être justifiées ». */}

      {/* L'encadré orange « Lecture contrôle carburant » expliquait, en bas
          d'écran, que le stock représente les cuves internes et que les
          sorties vers engins le diminuent. La carte « Cuves internes », tout
          en haut, dit exactement cela — au-dessus des cuves en question. */}

      {/* Le journal dit ce qui est entré et sorti; l'onglet Écarts dit ce que
          le système refuse de compter tant que personne n'a tranché. Les deux
          se lisent depuis le même écran parce qu'un écart se règle avec le
          stock sous les yeux. */}
      <div ref={journalRef} className="bento-card overflow-hidden">
        <Tabs value={searchParams.get('tab') === 'variances' ? 'variances' : 'movements'} onValueChange={(tab) => setSearchParams((current) => { const next = new URLSearchParams(current); next.set('tab', tab); return next; })}>
          <div className="flex flex-col gap-3 border-b border-border/70 px-4 pb-3 pt-4 md:px-5 md:pt-5 lg:flex-row lg:items-center lg:justify-between">
            <TabsList className="self-start">
              <TabsTrigger value="movements">{pageCopy.recentTitle}</TabsTrigger>
              <TabsTrigger value="variances">
                Écarts{pendingVarianceCount ? ` (${pendingVarianceCount})` : ''}
              </TabsTrigger>
            </TabsList>
            {/* Recherche du journal: pleine largeur sous les onglets sur
                petit écran, courte et sur leur ligne dès 1024 px. */}
            {searchParams.get('tab') !== 'variances' && movements.length > 0 ? (
              <Input
                data-testid="recherche-mouvements"
                className="w-full lg:w-[24rem] lg:max-w-[26rem]"
                aria-label="Rechercher un mouvement"
                placeholder={isControlFuel ? 'Rechercher sortie, cuve, engin, note...' : 'Rechercher par référence...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            ) : null}
          </div>

          <TabsContent
            value="movements"
            className="mt-0 px-4 pb-4 pt-4 md:px-5 md:pb-5"
          >
            {!isLoading && movements.length === 0 ? (
              // Illustration plutôt que glyphe: le journal vide occupe tout
              // l'onglet, et un dessin y explique mieux qu'une icône de 24 px
              // ce que la page contiendra une fois alimentée.
              <EmptyState
                illustration="donnee"
                title="Aucun mouvement enregistré"
                description={
                  isControlFuel
                    ? 'Chaque réception dans vos cuves et chaque litre servi à un engin s’inscrit ici, et c’est ce journal qui donne le stock disponible.'
                    : 'Chaque entrée et chaque sortie de volume s’inscrit ici, et c’est ce journal qui donne le stock disponible et le coût moyen au litre.'
                }
                actionLabel={pageCopy.newButton}
                onAction={ouvrirNouveauMouvement}
              />
            ) : (
              <StockMovementsTable
                movements={movements}
                isLoading={isLoading}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                rechercheExterne
                typeFilter={typeFilter}
                onClearTypeFilter={() => setTypeFilter(null)}
                onEdit={openEditModal}
                onDelete={confirmDelete}
                variant={isControlFuel ? 'controlFuel' : 'transport'}
              />
            )}
          </TabsContent>

          <TabsContent
            value="variances"
            className="mt-0 px-4 pb-4 pt-4 md:px-5 md:pb-5"
          >
            {varianceTripId && (
              <div className="mb-3 flex items-center justify-between text-sm">
                <span>Écarts du trajet sélectionné</span>
                <Button variant="link" onClick={() => setSearchParams({ tab: 'variances' })}>Voir tous les écarts</Button>
              </div>
            )}
            <ShortagePanel key={varianceTripId || "all"} tripId={varianceTripId || undefined} />
            <VolumeVariancePanel
              reconciliations={varianceTripId ? reconciliations.filter((item) => item.reference_type === 'trip' && item.reference_id === varianceTripId) : reconciliations}
              isLoading={isLoadingVariances}
              onDecide={openVarianceDecision}
              pagination={variancePagination}
            />
          </TabsContent>
        </Tabs>
      </div>

      <VolumeVarianceDecisionDialog
        decision={varianceDecision}
        onOpenChange={(open) => {
          if (!open) setVarianceDecision(null);
        }}
        onConfirm={submitVarianceDecision}
      />

      <Dialog open={showNewModal} onOpenChange={setShowNewModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden p-0">
          <DialogHeader>
            <DialogTitle className="px-6 pt-6">{pageCopy.dialogTitle}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[calc(90vh-132px)] space-y-3 overflow-y-auto px-6 py-3">
            <div className="space-y-1">
              <Label>Type</Label>
              <Select
                value={formData.movement_type}
                onValueChange={changerTypeMouvement}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  {(isControlFuel ? controlFuelMovementOptions : transportMovementOptions).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Quantité (L)</Label>
              <Input
                value={formData.quantity_liters}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    quantity_liters: e.target.value,
                  }))
                }
              />
            </div>
            {/* Le trajet reste au premier plan: c'est lui qui explique le
                mouvement, et le rattacher plus tard demande de retrouver la
                ligne dans le journal. */}
            {!isControlFuel && (
              <div className="space-y-1">
                <Label>Trajet (optionnel)</Label>
                <SearchableSelect
                  placeholder="Aucun trajet lié"
                  searchPlaceholder="Rechercher un trajet…"
                  emptyMessage="Aucun trajet trouvé"
                  value={formData.trip_id || NO_LINKED_RECORD}
                  onValueChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      trip_id: value === NO_LINKED_RECORD ? '' : value,
                    }))
                  }
                  options={linkedTripOptions}
                />
              </div>
            )}

            {/* Sept champs sur dix ne changent rien au volume ni au stock: le
                coût sort du barème, la date est celle de l'enregistrement, le
                trajet du carburant se déduit de la nature du mouvement. Ils
                restent tous là, sous une ligne, pour la saisie qui en a besoin. */}
            <ChampsAvances
              titre="Plus de détails"
              consequence="Coût, emplacements, date antidatée, commande et références."
              nombre={isControlFuel ? 6 : 7}
              valeurs={[
                // Seul ce qu'un humain a posé rouvre le repli: un coût venu du
                // barème ou un emplacement déduit du type ne sont pas des
                // saisies, et ouvriraient le bloc à chaque mouvement.
                coutTouche ? formData.unit_cost : '',
                emplacementsTouches ? formData.from_location : '',
                emplacementsTouches ? formData.to_location : '',
                formData.movement_date,
                formData.order_id,
                formData.reference_number,
                formData.notes,
              ]}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>{isControlFuel ? 'Coût interne / L' : 'Coût / L'}</Label>
                  <Input
                    value={formData.unit_cost}
                    onChange={(e) => {
                      setCoutTouche(true);
                      setFormData((prev) => ({
                        ...prev,
                        unit_cost: e.target.value,
                      }));
                    }}
                  />
                  {nomBareme && !coutTouche ? (
                    <p className="text-xs text-muted-foreground">
                      Barème «&nbsp;{nomBareme}&nbsp;» — corrigez-le si le prix négocié diffère.
                    </p>
                  ) : null}
                </div>
                <div className="space-y-1">
                  <Label>Date</Label>
                  <Input
                    type="datetime-local"
                    value={formData.movement_date}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        movement_date: e.target.value,
                      }))
                    }
                  />
                  {/* Le serveur date le mouvement de son enregistrement: ce
                      champ ne sert qu'à rattraper une saisie faite après coup. */}
                  <p className="text-xs text-muted-foreground">
                    Vide&nbsp;: daté de maintenant.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>{isControlFuel ? 'Depuis' : 'Origine'}</Label>
                  <Select
                    value={formData.from_location}
                    onValueChange={(value) => {
                      setEmplacementsTouches(true);
                      setFormData((prev) => ({ ...prev, from_location: value }));
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Origine" />
                    </SelectTrigger>
                    <SelectContent>
                      {(isControlFuel ? controlFuelOrigins : emplacementsTransport).map(([value, label]) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>{isControlFuel ? 'Vers' : 'Destination'}</Label>
                  <Select
                    value={formData.to_location}
                    onValueChange={(value) => {
                      setEmplacementsTouches(true);
                      setFormData((prev) => ({ ...prev, to_location: value }));
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Destination" />
                    </SelectTrigger>
                    <SelectContent>
                      {(isControlFuel ? controlFuelDestinations : emplacementsTransport).map(([value, label]) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {!isControlFuel && (
                <div className="space-y-1">
                  <Label>Commande (optionnel)</Label>
                  <SearchableSelect
                    placeholder="Aucune commande liée"
                    searchPlaceholder="Rechercher une commande ou un client…"
                    emptyMessage="Aucune commande trouvée"
                    value={formData.order_id || NO_LINKED_RECORD}
                    onValueChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        order_id: value === NO_LINKED_RECORD ? '' : value,
                      }))
                    }
                    options={linkedOrderOptions}
                  />
                </div>
              )}
              <div className="space-y-1">
                <Label>Référence</Label>
                <Input
                  value={formData.reference_number}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      reference_number: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>Notes</Label>
                <Input
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, notes: e.target.value }))
                  }
                />
              </div>
            </ChampsAvances>

            <BusinessActionContract
              title={`${LEXIQUE.conditionsAction} ce mouvement`}
              contract={movementContract}
              blockers={movementBlockers}
              compact
            />
          </div>
          <DialogFooter className="border-t bg-card px-6 py-4">
            <Button variant="outline" onClick={() => setShowNewModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreate} disabled={movementBlockers.length > 0}>
              Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden p-0">
          <DialogHeader>
            <DialogTitle className="px-6 pt-6">{pageCopy.editTitle}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[calc(90vh-132px)] space-y-3 overflow-y-auto px-6 py-3">
            <div className="space-y-1">
              <Label>Type</Label>
              <Select
                value={formData.movement_type}
                onValueChange={(value) =>
                  setFormData((prev) => ({ ...prev, movement_type: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  {(isControlFuel ? controlFuelMovementOptions : transportMovementOptions).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Quantité (L)</Label>
                <Input
                  value={formData.quantity_liters}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      quantity_liters: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>{isControlFuel ? 'Coût interne / L' : 'Coût / L'}</Label>
                <Input
                  value={formData.unit_cost}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      unit_cost: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>Date</Label>
                <Input
                  type="datetime-local"
                  value={formData.movement_date}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      movement_date: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>{isControlFuel ? 'Depuis' : 'Origine'}</Label>
                <Select
                  value={formData.from_location}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, from_location: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Origine" />
                  </SelectTrigger>
                <SelectContent>
                  {(isControlFuel ? controlFuelOrigins : emplacementsTransport).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              </div>
              <div className="space-y-1">
                <Label>{isControlFuel ? 'Vers' : 'Destination'}</Label>
                <Select
                  value={formData.to_location}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, to_location: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Destination" />
                  </SelectTrigger>
                <SelectContent>
                  {(isControlFuel ? controlFuelDestinations : emplacementsTransport).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              </div>
            </div>
            {!isControlFuel && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Trajet (optionnel)</Label>
                <SearchableSelect
                  placeholder="Aucun trajet lié"
                  searchPlaceholder="Rechercher un trajet…"
                  emptyMessage="Aucun trajet trouvé"
                  value={formData.trip_id || NO_LINKED_RECORD}
                  onValueChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      trip_id: value === NO_LINKED_RECORD ? '' : value,
                    }))
                  }
                  options={linkedTripOptions}
                />
              </div>
              <div className="space-y-1">
                <Label>Commande (optionnel)</Label>
                <SearchableSelect
                  placeholder="Aucune commande liée"
                  searchPlaceholder="Rechercher une commande ou un client…"
                  emptyMessage="Aucune commande trouvée"
                  value={formData.order_id || NO_LINKED_RECORD}
                  onValueChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      order_id: value === NO_LINKED_RECORD ? '' : value,
                    }))
                  }
                  options={linkedOrderOptions}
                />
              </div>
              </div>
            )}
            <div className="space-y-1">
              <Label>Référence</Label>
              <Input
                value={formData.reference_number}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    reference_number: e.target.value,
                  }))
                }
              />
            </div>
            <div className="space-y-1">
              <Label>Notes</Label>
              <Input
                value={formData.notes}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, notes: e.target.value }))
                }
              />
            </div>
            <BusinessActionContract
              title={`${LEXIQUE.conditionsAction} ce mouvement`}
              contract={movementContract}
              blockers={movementBlockers}
              compact
            />
          </div>
          <DialogFooter className="border-t bg-card px-6 py-4">
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleUpdate} disabled={movementBlockers.length > 0}>
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Supprimer le mouvement</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Confirmer la suppression du mouvement{' '}
            <span className="font-semibold">
              {referenceLisible(selectedMovement?.reference_number, 'sans référence')}
            </span>
            ?
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
              Annuler
            </Button>
            <Button
              className="bg-destructive hover:bg-destructive"
              onClick={handleDelete}
            >
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* Écarts de volume — le verdict est celui du serveur (`core/volume_variance`,
   deux bornes: litres ET pourcentage). L'écran ne rejuge rien, il expose le
   verdict, la mesure et les seuils qui l'ont produit, puis demande une
   décision tracée. */

// Libellés et teintes des verdicts: domaine `varianceStatut` de lib/libelles.js.
// `incomplete` n'est pas un écart: c'est l'aveu qu'il manque un chiffre pour en
// calculer un. La colonne restée à « — » désigne le volume qui manque.

const REFERENCE_LABELS = { trip: 'Trajet', order: 'Commande', delivery: 'Livraison' };

// Le contrôle ne retient un écart que s'il franchit les deux seuils réglés
// (litres ET pourcentage), comme le téléphone du chauffeur.
const seuilsEcart = (item) => {
  const litres = Number(item.tolerance_quantity) > 0 ? `${formatNombre(item.tolerance_quantity)} L` : null;
  const pourcent = Number(item.tolerance_percent) > 0 ? formatPourcent(item.tolerance_percent, 1) : null;
  if (litres && pourcent) return `Seuils : ${litres} et ${pourcent}`;
  if (litres || pourcent) return `Seuil : ${litres || pourcent}`;
  return 'Aucun seuil : toute différence compte';
};

function ConcerneEcart({ item }) {
  if (item.reference_type !== 'trip') {
    return (
      <p className="font-medium text-foreground">
        {REFERENCE_LABELS[item.reference_type] || 'Autre opération'}
      </p>
    );
  }
  const plaques = plaquesTrajet(item);
  return (
    <>
      <Link to={`/trips/${item.reference_id}`} className="font-medium text-primary underline-offset-2 hover:underline">
        {referenceTrajet(item)}
      </Link>
      {plaques && <p className="text-xs text-muted-foreground">{plaques}</p>}
    </>
  );
}

function volumesEcart(item) {
  return {
    baseline: item.loaded_volume ?? item.ordered_volume ?? item.planned_volume,
    compared: item.delivered_volume ?? item.invoiced_volume ?? item.received_stock_volume,
  };
}

export function VolumeVariancePanel({ reconciliations, isLoading, onDecide, pagination }) {
  if (isLoading) {
    return <LoadingState shape="table" />;
  }

  if (!reconciliations.length) {
    return (
      <EmptyState
        variant="empty"
        icon={AlertTriangle}
        title="Aucun écart de volume"
        description="Un écart apparaît ici dès qu'un chargement, une livraison ou une facture ne concorde pas avec la mesure attendue."
      />
    );
  }

  // Plus de carte ici: l'onglet est déjà posé dans une carte. Deux cadres
  // imbriqués donnaient un double liseré et deux rayons différents.
  return (
    <div className="mt-4 space-y-2">
    <div>
      <div className="flex items-center gap-1">
        <h3 className="font-semibold">Écarts de volume au déchargement</h3>
        <Aide titre="Écarts de volume au déchargement">Contrôle automatique de chaque livraison : volume chargé comparé au volume livré. Un écart n’est retenu que s’il dépasse les deux seuils de la société. Distinct du manquant ci-dessus, qui fait le bilan complet du trajet.</Aide>
      </div>
    </div>
    <ul className="space-y-3 lg:hidden" data-testid="variance-cards">
      {reconciliations.map((item) => {
        const decided = ['approved', 'rejected'].includes(item.status);
        const { baseline, compared } = volumesEcart(item);
        return (
          <li key={item.id} data-testid="variance-card" className="min-w-0 space-y-3 rounded-lg border p-3">
            <div className="min-w-0 break-words"><ConcerneEcart item={item} /></div>
            <dl className="grid grid-cols-3 gap-2 text-sm">
              <div className="min-w-0"><dt className="text-xs text-muted-foreground">Attendu</dt><dd className="tabular-nums">{baseline == null ? '—' : formatVolume(baseline)}</dd></div>
              <div className="min-w-0"><dt className="text-xs text-muted-foreground">Constaté</dt><dd className="tabular-nums">{compared == null ? '—' : formatVolume(compared)}</dd></div>
              <div className="min-w-0"><dt className="text-xs text-muted-foreground">Écart</dt><dd className="tabular-nums">{formatVolume(item.primary_variance_quantity)}<span className="block text-xs text-muted-foreground">{formatPourcent(item.primary_variance_percent || 0, 2)}</span></dd></div>
            </dl>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <StatutBadge domaine="varianceStatut" code={item.status} repli="À examiner" />
              <span className="text-xs text-muted-foreground">{seuilsEcart(item)}</span>
            </div>
            {decided ? (
              <p className="text-xs text-muted-foreground">{item.approval_comment || 'Sans motif enregistré'}</p>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <Button className="h-10 md:h-10 w-full" variant="outline" onClick={() => onDecide(item, 'approve')}>Approuver</Button>
                <Button className="h-10 md:h-10 w-full" variant="outline" onClick={() => onDecide(item, 'reject')}>Refuser</Button>
              </div>
            )}
          </li>
        );
      })}
    </ul>
    <div className="hidden lg:block" data-testid="variance-table">
    <Table stickyFirstColumn={false}>
      <TableHeader>
        <TableRow>
          <TableHead className="px-2">Concerne</TableHead>
          <TableHead className="px-2">Attendu</TableHead>
          <TableHead className="px-2">Constaté</TableHead>
          <TableHead className="px-2">Écart</TableHead>
          <TableHead className="px-2">Verdict</TableHead>
          <TableHead className="px-2 text-right">Décision</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {reconciliations.map((item) => {
          const decided = ['approved', 'rejected'].includes(item.status);
          const { baseline, compared } = volumesEcart(item);
          return (
            <TableRow key={item.id}>
              <TableCell className="min-w-0 break-words px-2">
                <ConcerneEcart item={item} />
              </TableCell>
              <TableCell className="px-2 tabular-nums whitespace-nowrap">
                {baseline == null ? '—' : formatVolume(baseline)}
              </TableCell>
              <TableCell className="px-2 tabular-nums whitespace-nowrap">
                {compared == null ? '—' : formatVolume(compared)}
              </TableCell>
              <TableCell className="px-2 tabular-nums whitespace-nowrap">
                <p>{formatVolume(item.primary_variance_quantity)}</p>
                <p className="text-xs text-muted-foreground">
                  {formatPourcent(item.primary_variance_percent || 0, 2)}
                </p>
              </TableCell>
              <TableCell className="px-2">
                <StatutBadge domaine="varianceStatut" code={item.status} repli="À examiner" />
                <p className="mt-1 text-xs text-muted-foreground">
                  {seuilsEcart(item)}
                </p>
              </TableCell>
              <TableCell className="px-2 text-right">
                {decided ? (
                  <p className="text-xs text-muted-foreground">
                    {item.approval_comment || 'Sans motif enregistré'}
                  </p>
                ) : (
                  <div className="flex justify-end gap-1.5">
                    <Button size="sm" variant="outline" onClick={() => onDecide(item, 'approve')}>
                      Approuver
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => onDecide(item, 'reject')}>
                      Refuser
                    </Button>
                  </div>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
    </div>
    {pagination ? <BarrePagination pagination={pagination} nom="écarts" /> : null}
    </div>
  );
}

function VolumeVarianceDecisionDialog({ decision, onOpenChange, onConfirm }) {
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const reconciliationId = decision?.reconciliation?.id;

  // Le motif se vide quand une autre ligne est ouverte. Passer par une `key`
  // dérivée du texte saisi remonterait le composant à chaque frappe.
  useEffect(() => {
    setReason('');
  }, [reconciliationId, decision?.action]);

  const isApproval = decision?.action === 'approve';

  return (
    <AlertDialog open={Boolean(decision)} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {isApproval ? 'Approuver cet écart' : 'Refuser cet écart'}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {isApproval
              ? 'L’écart sera marqué comme accepté. La facturation et le paiement restent autorisés indépendamment de cette décision. Le motif est conservé.'
              : 'Le refus signale un écart contesté, sans suspendre les opérations. Le motif est conservé dans l’historique.'}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-2">
          <p className="text-sm tabular-nums text-foreground">
            {formatVolume(decision?.reconciliation?.primary_variance_quantity)} ·{' '}
            {formatPourcent(decision?.reconciliation?.primary_variance_percent || 0, 2)}
          </p>
          <Textarea
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Motif de la décision (obligatoire)"
            aria-label="Motif de la décision"
          />
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>Annuler</AlertDialogCancel>
          <AlertDialogAction
            disabled={!reason.trim() || isSubmitting}
            onClick={async (event) => {
              // Radix ferme la boîte au clic; on garde la main jusqu'à la
              // réponse serveur pour ne pas afficher un succès prématuré.
              event.preventDefault();
              setIsSubmitting(true);
              try {
                await onConfirm(reason.trim());
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isApproval ? 'Approuver' : 'Refuser'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default StockPage;
